import { useEffect } from 'react';
import type { ReactNode } from 'react';
import type { Channel } from 'pusher-js';
import { usePusher } from 'hooks/usePusher';
import { useTranslation } from 'i18n';
import {
  MENU_DUPLICATION_FAILED,
  MENU_DUPLICATION_SUCCESSFUL,
  MENU_IMPORT_FAILED,
  MENU_IMPORT_SUCCESSFUL,
  MENU_NOTIFICATION_EVENT,
  MENU_NOTIFICATION_TYPE,
} from 'constants/menuManagement';

const NS = 'menuManagement';

type UseMenuNotificationsParams = {
  companyId?: string | number;
  onError?: (message?: ReactNode) => void;
  onMenusRefresh?: () => Promise<void> | void;
  onSuccess?: (message?: ReactNode) => void;
};

type MenuNotificationDetail = {
  message?: string;
  type: string;
};

export const useMenuNotifications = ({
  companyId,
  onError,
  onMenusRefresh,
  onSuccess,
}: UseMenuNotificationsParams) => {
  const { t } = useTranslation(NS, { useSuspense: false });
  const { channels }: { channels: Channel[] | null } = usePusher();

  useEffect(() => {
    if (!companyId || !channels) return undefined;

    const channel = channels.find(
      ch => ch.name === `${import.meta.env.VITE_WEB_SOCKET_STAGE}-company-${companyId}`,
    );

    if (!channel) return undefined;

    channel.unbind(MENU_DUPLICATION_SUCCESSFUL);
    channel.unbind(MENU_DUPLICATION_FAILED);
    channel.unbind(MENU_IMPORT_SUCCESSFUL);
    channel.unbind(MENU_IMPORT_FAILED);

    channel.bind(MENU_DUPLICATION_SUCCESSFUL, async () => {
      await onMenusRefresh?.();
      onSuccess?.(
        <span data-testid="menu-copy-success-notification">
          {t('useMenuNotifications.menuCopySuccess')}
        </span>,
      );
    });

    channel.bind(MENU_DUPLICATION_FAILED, () => {
      onError?.(
        <span data-testid="menu-copy-failed-notification">
          {t('useMenuNotifications.menuCopyFailed')}
        </span>,
      );
    });

    channel.bind(MENU_IMPORT_SUCCESSFUL, async () => {
      await onMenusRefresh?.();
      onSuccess?.(
        <span data-testid="menu-import-success-notification">
          {t('useMenuNotifications.menuImportSuccess')}
        </span>,
      );
    });

    channel.bind(MENU_IMPORT_FAILED, () => {
      onError?.(
        <span data-testid="menu-import-failed-notification">
          {t('useMenuNotifications.menuImportFailed')}
        </span>,
      );
    });

    return () => {
      channel.unbind(MENU_DUPLICATION_SUCCESSFUL);
      channel.unbind(MENU_DUPLICATION_FAILED);
      channel.unbind(MENU_IMPORT_SUCCESSFUL);
      channel.unbind(MENU_IMPORT_FAILED);
    };
  }, [channels, companyId, onSuccess, onError, onMenusRefresh, t]);

  useEffect(() => {
    const handleMenuNotification = (event: Event) => {
      const { message, type } = (event as CustomEvent<MenuNotificationDetail>).detail;
      // Some dispatchers (e.g. item-save) omit message and show their own toast separately.
      if (!message) return;
      if (type === MENU_NOTIFICATION_TYPE.SUCCESS) {
        onSuccess?.(message);
      } else if (type === MENU_NOTIFICATION_TYPE.ERROR) {
        onError?.(message);
      }
    };

    window.addEventListener(MENU_NOTIFICATION_EVENT, handleMenuNotification);
    return () => window.removeEventListener(MENU_NOTIFICATION_EVENT, handleMenuNotification);
  }, [onSuccess, onError]);
};
